/**
 * Complex process step recording page with React Flow diagram
 * Layout and diagram logic extracted to separate modules for better maintainability
 */
import { useMemo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from '@tanstack/react-router';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useStepRecordingHeartbeat } from '@/hooks/useStepRecordingHeartbeat';
import { useProcessStepRecording } from '@/api/queries/useRecording';
import { notFoundOrUnknownKey } from '@/api/errors';
import { toastApiError } from '@/api/mutations/toast-api-error';
import {
  useUpdateStepRecording,
  useStopRecording,
  useFinalizeRecording,
  useResumeRecording
} from '@/api/mutations/useRecording';
import { StepReviewDialog } from './components/StepReviewDialog';
import { useProcess } from '@/api/queries/useProcesses';
import { readDialogLoadState } from '@/lib/dialogLoadState';
import { useOrgMembersForLookup } from '@/api/queries/useOrgMembers';
import { useSystemsForLookup } from '@/api/queries/useSystems';
import { useOrgFunctionsForLookup } from '@/api/queries/useOrgFunctions';
import { useExternalContactsForLookup } from '@/api/queries/useExternalContacts';
import { useArtifacts } from '@/api/queries/useArtifacts';
import { useOrgSettings } from '@/api/queries/useOrgSettings';
import { useAiEnabled } from '@/hooks/useAiEnabled';
import { usePermissions } from '@/hooks/usePermissions';
import { useProcessRecording } from './hooks/useProcessRecording';
import { useReassignFlow } from './hooks/useReassignFlow';
import { useStepFinishedCard } from './hooks/useStepFinishedCard';
import { useProcessDiagramData } from './hooks/useProcessDiagramData';
import { ProcessRecordingBottomBar } from './components/ProcessRecordingBottomBar';
import { ProcessDiagram } from './components/ProcessDiagram';
import { EndlessProcessLoader } from '@/pages/processes/components/endless-process/EndlessProcessLoader';
import { useLoaderFinish } from '@/pages/processes/components/endless-process/finishGate';
import {
  BliksyChatButton,
  BliksyChatPanel,
  BLIKSY_DEFAULT_WIDTH,
  BLIKSY_PANEL_MARGIN,
  clampBliksyWidth
} from '@/components/bliksy';
import { StepRecordingDialogs } from './components/StepRecordingDialogs';
import {
  readSurveyToggles,
  type EditProcessStepRecordingRequest,
  type ProcessStepRecordingDisplay,
  type StepInput,
  type StepDecisionRule
} from '@bliks-io/contracts';
import { Page, PageContent, PageFooter, type StartEventContent } from '@bliks-io/ui';
import { getDisabledReasons } from './utils/getDisabledReasons';

// Persisted resizable width of the Bliksy chat panel.
const BLIKSY_WIDTH_STORAGE_KEY = 'bliks:bliksy-panel-width';

function loadBliksyWidth(): number {
  const stored = Number(localStorage.getItem(BLIKSY_WIDTH_STORAGE_KEY));
  return Number.isFinite(stored) && stored > 0 ? clampBliksyWidth(stored) : BLIKSY_DEFAULT_WIDTH;
}

// The chat session belongs to the recorder — the backend rejects everyone
// else (except org managers), so don't render a panel that can only 403.
// The permissions can() map is type-level permissive for Update/Recording;
// mirror the backend's recordedBy condition directly instead.
function canAccessBliksySession(
  recordedBy: string | { _id: string } | null | undefined,
  userId: string | undefined,
  userTypes: readonly string[]
): boolean {
  const recordedById =
    typeof recordedBy === 'object' && recordedBy !== null ? recordedBy._id : recordedBy;
  return !!recordedById && (recordedById === userId || userTypes.includes('org_manager'));
}

export function StepRecordingPage() {
  const { t } = useTranslation();
  const { recordingId, slug } = useParams({
    from: '/$slug/processes/recording/step-recording/$recordingId'
  });

  const { data: orgSettings } = useOrgSettings();
  const { stepNameGenerationEnabled: aiForStepNames } = useAiEnabled();

  // Fetch recording and related data
  const {
    data: recordingData,
    isLoading: recordingLoading,
    error: recordingError
  } = useProcessStepRecording(recordingId);
  const currentRecording =
    recordingData && 'processStepRecording' in recordingData
      ? recordingData.processStepRecording
      : null;

  const processQuery = useProcess(currentRecording?.process || '');
  const processData = processQuery.data;
  const currentProcess = processData?.process;
  const entitySuggestions = processData?.entitySuggestions;
  const processEvents = processData?.events;

  const completedProcessSteps = useMemo(() => {
    if (!currentProcess?.processStepRecordings || !currentRecording) return [];
    return (currentProcess.processStepRecordings as ProcessStepRecordingDisplay[]).filter(
      (step) => step.status === 'finalized' && step._id !== currentRecording._id
    );
  }, [currentProcess?.processStepRecordings, currentRecording]);

  // The pending `ProcessStepAssignment` for the user's own in-flight recording.
  // When the recorder reassigns themselves to unclear, this row flips to
  // `pending-unclear` (briefly) or `rejected-unclear`; forwarded to the preview
  // so the flow renders the corresponding amber "?" state for the current step.
  const currentRecordingStatus = useMemo(() => {
    if (!currentRecording || !processData?.pendingAssignments) return undefined;
    const match = processData.pendingAssignments.find(
      (a) => a.processStepRecording === currentRecording._id
    );
    return match?.status;
  }, [currentRecording, processData?.pendingAssignments]);

  // Lookup variants include soft-deleted entities so refs on old recordings
  // still resolve to names (rendered grayed with a "(deleted)" marker).
  const { data: orgMembers } = useOrgMembersForLookup();
  const { data: systems } = useSystemsForLookup();
  const { data: orgFunctions } = useOrgFunctionsForLookup();
  const { data: externalContacts } = useExternalContactsForLookup();
  const { data: artifacts } = useArtifacts();

  useDocumentTitle('pageTitles.stepRecording', { name: currentProcess?.name || 'Loading...' });

  // Mutations
  const updateRecording = useUpdateStepRecording();
  const stopRecording = useStopRecording();
  const finalizeRecording = useFinalizeRecording();
  const resumeRecording = useResumeRecording();
  const stepFinishedCard = useStepFinishedCard({ slug, processName: currentProcess?.name });

  // Track active editing time: beat only while the recording is in_progress.
  // The post-stop AI-review wait (pending_finalization) is excluded by design.
  useStepRecordingHeartbeat(recordingId, currentRecording?.status === 'in_progress');

  // Bliksy chat state
  const [isBliksyOpen, setIsBliksyOpen] = useState(true);
  const [bliksyWidth, setBliksyWidth] = useState(loadBliksyWidth);
  // setBliksyWidth tracks the drag live (reflowing the diagram); persistence
  // waits for the commit so we don't hammer localStorage on every pointer move.
  const handleBliksyWidthCommit = useCallback((width: number) => {
    localStorage.setItem(BLIKSY_WIDTH_STORAGE_KEY, String(width));
  }, []);

  // Whether any post-recording survey is enabled — decides whether the
  // step-finished card offers the survey. Reads `classic` only:
  // the recorder authored this step, and the author is never asked the Variant
  // Check (ADR 0022), so a variant-only tenant has no survey to send them to.
  const anySurveyEnabled = readSurveyToggles(orgSettings).classic;

  // Check if Bliksy chat is enabled (requires both master AI toggle and Bliksy sub-toggle)
  const { bliksyEnabled } = useAiEnabled();
  const { authContext, userTypes } = usePermissions();
  const canUseBliksyChat = canAccessBliksySession(
    currentRecording?.recordedBy,
    authContext?.userId,
    userTypes
  );

  // Stable callbacks for useProcessRecording
  const handleUpdateRecording = useCallback(
    async (updates: EditProcessStepRecordingRequest) => {
      if (!recordingId) return;
      await updateRecording.mutateAsync({
        recordingId,
        data: updates
      });
    },
    [recordingId, updateRecording]
  );

  // Stop transitions the recording into pending_finalization, which causes
  // the review dialog (below) to open via derived state. The actual finalize
  // happens when the user clicks Apply inside that dialog.
  const handleStop = useCallback(async () => {
    if (!recordingId) return;
    await stopRecording.mutateAsync(recordingId);
  }, [recordingId, stopRecording]);

  // Process recording hook for business logic
  const {
    showAssignInsteadModal,
    setShowAssignInsteadModal,
    showProcessDetailsModal,
    setShowProcessDetailsModal,
    showProcessFlowModal,
    setShowProcessFlowModal,
    showInputModal,
    setShowInputModal,
    showDecisionTableModal,
    setShowDecisionTableModal,
    editingInputIndex,
    setEditingInputIndex,
    handleFieldChange,
    handleFieldChangeDebounced,
    flushPendingFieldChanges,
    handleWhoElseDoesThisChange,
    handleSuccessorChange,
    handleAddInput,
    handleSaveInput,
    handleEditInput,
    handleRemoveInput,
    handleSaveDecisionRules,
    handleOpenDecisionTable,
    handleFinishRecording,
    hasRequiredInput,
    hasValidTaskDescription,
    hasValidSuccessors,
    hasValidDecisionRules,
    hasValidOutput
  } = useProcessRecording(currentRecording || null, handleUpdateRecording, handleStop);

  const {
    showConfirmReassignDialog,
    pendingReassignData,
    suggestionSession,
    handleSuccessorsSubmit,
    handleConfirmReassign,
    handleCancelReassign,
    reassignPending
  } = useReassignFlow({ recordingId, slug, setShowAssignInsteadModal });

  // show-process only returns suggestions already referenced server-side; a
  // suggestion created inside the reassign picker isn't (its ref is staged
  // client-side until the reassign POST), so merge the flow session's created
  // docs in for the dialogs' proposed-name lookups.
  const dialogEntitySuggestions = useMemo(
    () => suggestionSession.mergeInto(entitySuggestions),
    [suggestionSession, entitySuggestions]
  );

  // Apply = the user accepted (possibly edited) AI suggestion. We finalize on
  // the server, then show the step-finished card, which routes to My Tasks
  // (optionally with a survey id to auto-open the feedback dialog).
  const { celebrate: celebrateStep } = stepFinishedCard;
  const handleApply = useCallback(
    async (vals: { name: string; description: string }) => {
      if (!recordingId) return;
      try {
        const result = await finalizeRecording.mutateAsync({
          recordingId,
          body: vals
        });
        celebrateStep(anySurveyEnabled && result?.survey ? result.survey._id : undefined);
      } catch (error) {
        console.error('Failed to finalize:', error);
        toastApiError(error, 'stepRecording.finishError');
      }
    },
    [recordingId, finalizeRecording, anySurveyEnabled, celebrateStep]
  );

  // Success counts as in flight: the review dialog stays locked until the
  // step-finished card replaces it.
  const finalizeInFlight = finalizeRecording.isPending || finalizeRecording.isSuccess;

  // Back = the user wants to keep editing. Returns to in_progress; generated
  // fields are preserved so a follow-up Stop reuses them. Esc, the overlay and
  // the header X land here too, so a running resume or finalize blocks it.
  const handleBackToEditing = useCallback(async () => {
    if (!recordingId || resumeRecording.isPending || finalizeInFlight) return;
    try {
      await resumeRecording.mutateAsync(recordingId);
    } catch (error) {
      console.error('Failed to resume:', error);
      toastApiError(error, 'stepRecording.resumeError');
    }
  }, [recordingId, resumeRecording, finalizeInFlight]);

  const [isFinishing, setIsFinishing] = useState(false);
  const handleFinish = useCallback(async () => {
    setIsFinishing(true);
    try {
      await handleFinishRecording();
    } catch (error) {
      console.error('Failed to stop recording:', error);
      toastApiError(error, 'stepRecording.stopError');
    } finally {
      setIsFinishing(false);
    }
  }, [handleFinishRecording]);

  const showReviewDialog = currentRecording?.status === 'pending_finalization';

  // Validation for finish button — reasons drives both the tooltip and the
  // enabled/disabled state, so they can never disagree.
  const disabledReasons = useMemo(
    () =>
      getDisabledReasons(currentRecording, {
        hasRequiredInput,
        hasValidTaskDescription,
        hasValidSuccessors,
        hasValidDecisionRules,
        hasValidOutput
      }),
    [
      currentRecording,
      hasRequiredInput,
      hasValidTaskDescription,
      hasValidSuccessors,
      hasValidDecisionRules,
      hasValidOutput
    ]
  );
  const isReadyToFinish = currentRecording !== null && disabledReasons.length === 0;

  // Memoize handlers object to prevent unnecessary recalculations
  const handlers = useMemo(
    () => ({
      handleFieldChange,
      handleFieldChangeDebounced,
      flushPendingFieldChanges,
      handleAddInput,
      handleEditInput,
      handleRemoveInput,
      handleOpenDecisionTable,
      handleSuccessorChange,
      handleWhoElseDoesThisChange
    }),
    [
      handleFieldChange,
      handleFieldChangeDebounced,
      flushPendingFieldChanges,
      handleAddInput,
      handleEditInput,
      handleRemoveInput,
      handleOpenDecisionTable,
      handleSuccessorChange,
      handleWhoElseDoesThisChange
    ]
  );

  const { layoutedNodes, styledEdges, contentSignature } = useProcessDiagramData({
    currentRecording,
    currentProcess,
    events: processEvents,
    orgMembers,
    systems,
    externalContacts,
    artifacts,
    handlers
  });

  // Show loading state until ALL data is ready (prevents layout shifts from async data loading)
  const isLoading = [
    recordingLoading,
    !currentRecording,
    // isPending, not isLoading: offline the GET waits paused and is not
    // loading, yet the diagram still needs its events.
    Boolean(currentRecording?.process) && processQuery.isPending,
    !orgMembers,
    !systems,
    !orgFunctions,
    !externalContacts,
    !artifacts
  ].some(Boolean);
  const loadStatus = isLoading ? 'loading' : 'ready';
  const status = recordingError && !currentRecording ? 'failed' : loadStatus;
  const { showLoader, done, onFinished, episodeKey } = useLoaderFinish(status);

  if (status === 'failed') {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-destructive">
          {t(notFoundOrUnknownKey(recordingError, 'errors.processStep.notFound'))}
        </p>
      </div>
    );
  }

  if (showLoader) {
    return (
      <EndlessProcessLoader
        key={episodeKey}
        messageKey="viralRecording.status.loadingRecordingData"
        done={done}
        onFinished={onFinished}
      />
    );
  }

  return (
    <Page>
      <PageContent className="relative p-0">
        <ProcessDiagram
          initialNodes={layoutedNodes}
          initialEdges={styledEdges}
          contentSignature={contentSignature}
          // The panel is a left overlay; the canvas region it covers is its
          // margin plus its width. The diagram centers the focused step in the
          // region right of this inset. 0 when the panel is closed/disabled.
          leftInset={
            bliksyEnabled && canUseBliksyChat && isBliksyOpen
              ? bliksyWidth + BLIKSY_PANEL_MARGIN
              : 0
          }
        />
        {/* Bliksy Chat */}
        {bliksyEnabled && canUseBliksyChat && (
          <>
            <BliksyChatButton
              isOpen={isBliksyOpen}
              onToggle={() => setIsBliksyOpen(!isBliksyOpen)}
            />
            <BliksyChatPanel
              sessionId={recordingId}
              slug={slug}
              isOpen={isBliksyOpen}
              onClose={() => setIsBliksyOpen(false)}
              width={bliksyWidth}
              onWidthChange={setBliksyWidth}
              onWidthCommit={handleBliksyWidthCommit}
            />
          </>
        )}
      </PageContent>
      <PageFooter>
        <ProcessRecordingBottomBar
          onBack={() => window.history.back()}
          onAssignSomeoneElse={() => setShowAssignInsteadModal(true)}
          processName={currentProcess?.name}
          onViewProcessDetails={() => setShowProcessDetailsModal(true)}
          onViewProcessFlow={() => setShowProcessFlowModal(true)}
          isSaving={updateRecording.isPending}
          primaryAction={{
            label: isFinishing
              ? t('viralRecording.buttons.finishing')
              : t('viralRecording.buttons.finishRecording'),
            // Flushes any pending debounced field write before the stop POST
            // (handleStop runs as the hook's onFinish).
            onClick: handleFinish,
            loading: isFinishing,
            disabled: !isReadyToFinish || isFinishing,
            disabledReasons
          }}
          showAssignButton
        />
      </PageFooter>

      <StepRecordingDialogs
        currentProcess={currentProcess}
        processLoadState={readDialogLoadState([processQuery])}
        currentRecording={currentRecording}
        entitySuggestions={dialogEntitySuggestions}
        events={processEvents}
        currentRecordingStatus={currentRecordingStatus}
        completedProcessSteps={completedProcessSteps}
        showProcessDetailsModal={showProcessDetailsModal}
        setShowProcessDetailsModal={setShowProcessDetailsModal}
        showAssignInsteadModal={showAssignInsteadModal}
        setShowAssignInsteadModal={setShowAssignInsteadModal}
        handleSuccessorsSubmit={handleSuccessorsSubmit}
        suggestionSession={suggestionSession}
        showConfirmReassignDialog={showConfirmReassignDialog}
        pendingReassignData={pendingReassignData}
        handleCancelReassign={handleCancelReassign}
        handleConfirmReassign={handleConfirmReassign}
        reassignPending={reassignPending}
        showInputModal={showInputModal}
        setShowInputModal={setShowInputModal}
        editingInputIndex={editingInputIndex}
        setEditingInputIndex={setEditingInputIndex}
        handleSaveInput={handleSaveInput as (data: StepInput | StartEventContent) => void}
        showDecisionTableModal={showDecisionTableModal}
        setShowDecisionTableModal={setShowDecisionTableModal}
        handleSaveDecisionRules={handleSaveDecisionRules as (rules: StepDecisionRule[]) => void}
        showProcessFlowModal={showProcessFlowModal}
        setShowProcessFlowModal={setShowProcessFlowModal}
      />

      {currentRecording ? (
        <StepReviewDialog
          open={showReviewDialog && !stepFinishedCard.isOpen}
          recording={currentRecording}
          aiEnabled={aiForStepNames}
          onCancel={handleBackToEditing}
          onApply={handleApply}
          applyInFlight={finalizeInFlight}
          backInFlight={resumeRecording.isPending}
        />
      ) : null}
      {stepFinishedCard.element}
    </Page>
  );
}
